'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, IndianRupee, Clock, Zap, MessageSquare } from 'lucide-react';
import BadgeTierChip from '@/components/ui/BadgeTierChip';
import VerificationBadge from '@/components/ui/VerificationBadge';
import RatingRadarChart from './RatingRadarChart';
import RequestModal from './RequestModal';

const expertProfile = {
  id: 'expert-seed-001',
  pseudonym: 'Advisor #K9M',
  avatarSeed: 'Advisor #K9M',
  headline: 'Senior brand strategy and go-to-market leader with two decades of FMCG portfolio scaling across South Asia and Southeast Asia.',
  domainTags: ['FMCG', 'Go-to-Market', 'Brand Strategy', 'Portfolio Management', 'Channel Strategy'],
  sectorExposure: ['FMCG', 'Retail', 'E-commerce', 'Distribution', 'Modern Trade'],
  badgeTier: 'GOLD' as const,
  averageRating: 4.4,
  ratingCount: 18,
  hourlyRate: 22000,
  sessionRate: 40000,
  rushMultiplier: 1.5,
  verificationStatus: 'WEB_CHECKED' as const,
  experienceTier: 'PRINCIPAL',
  summaryBio: 'A seasoned commercial and marketing leader with nearly two decades of experience across FMCG, modern trade, and distribution-intensive businesses. This advisor has led large-scale brand portfolio transformations, channel strategy redesigns, and market entry programs across South and Southeast Asia. Known for translating complex category dynamics into clear commercial frameworks that drive measurable market share outcomes. Has operated at the P&L ownership level across multiple geographies and product categories. Brings both strategic rigour and operational depth to advisory engagements, with a track record of practical, implementable recommendations.',
  keyStrengths: [
    'Brand portfolio architecture and SKU rationalisation in competitive FMCG categories',
    'Channel strategy design for multi-tier distribution across South and Southeast Asia',
    'P&L ownership and margin improvement in high-velocity consumer goods portfolios',
    'Go-to-market playbook development for category launches and brand relaunches',
    'Commercial due diligence for consumer sector M&A and PE transactions',
  ],
  engagementTypes: ['Advisory', 'Due Diligence', 'Workshops', 'Strategic Review', 'Keynote'],
  webVerifySummary: 'Look for LinkedIn profiles showing senior marketing or commercial leadership at multinational FMCG companies, with tenure of 10+ years at the regional or national director level. Search for published articles on brand architecture or distribution strategy in Indian or Southeast Asian trade publications. Check for speaker credits at industry forums such as CII or FICCI marketing conferences. Membership in bodies like the Marketing Society of India or equivalent regional associations is a strong corroborating signal.',
  ratingDimensions: {
    relevance: 4.6,
    depth: 4.3,
    clarity: 4.5,
    responsiveness: 4.2,
    value: 4.4,
  },
};

export default function ExpertProfileContent() {
  const [requestOpen, setRequestOpen] = useState(false);

  const colors = ['#f0b429', '#2dd4bf', '#a78bfa', '#fb7185', '#34d399'];
  const colorIndex = expertProfile.avatarSeed.charCodeAt(expertProfile.avatarSeed.length - 1) % colors.length;
  const initial = expertProfile.pseudonym.replace('Advisor #', '').charAt(0);

  return (
    <>
      <div className="py-8">
        <Link
          href="/expert-browse-page"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8 group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Browse
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* Left / Main column */}
          <div className="lg:col-span-2 xl:col-span-3 flex flex-col gap-6">
            {/* Hero header */}
            <div className="surface-elevated p-6">
              <div className="flex items-start gap-5 flex-wrap">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center font-mono-data font-bold text-background text-3xl flex-shrink-0"
                  style={{ background: colors[colorIndex] }}
                  role="img"
                  aria-label={`Avatar for ${expertProfile.pseudonym}`}
                >
                  {initial}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <h1 className="font-mono-data text-2xl font-bold text-foreground">{expertProfile.pseudonym}</h1>
                    <BadgeTierChip tier={expertProfile.badgeTier} size="lg" />
                    <VerificationBadge
                      status={expertProfile.verificationStatus}
                      webVerifySummary={expertProfile.webVerifySummary}
                    />
                  </div>
                  <p className="text-xs font-mono-data text-muted-foreground mb-3">{expertProfile.experienceTier} · {expertProfile.ratingCount} engagements</p>
                  <p className="text-base text-foreground leading-relaxed">{expertProfile.headline}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-5">
                {expertProfile.domainTags.map((tag) => (
                  <span key={`profile-tag-${tag}`} className="text-xs font-mono-data px-3 py-1 rounded-full bg-primary/8 text-primary border border-primary/15">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-2 mt-4">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={`profile-star-${s}`}
                    size={16}
                    className={s <= Math.round(expertProfile.averageRating) ? 'text-primary fill-primary' : 'text-border'}
                    fill={s <= Math.round(expertProfile.averageRating) ? 'currentColor' : 'none'}
                  />
                ))}
                <span className="font-mono-data text-sm font-semibold text-foreground ml-1">{expertProfile.averageRating.toFixed(1)}</span>
                <span className="text-sm text-muted-foreground">({expertProfile.ratingCount} ratings)</span>
              </div>
            </div>

            {/* Summary Bio */}
            <div className="surface-elevated p-6">
              <h2 className="font-heading text-lg text-foreground mb-4">About This Advisor</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{expertProfile.summaryBio}</p>
            </div>

            {/* Key Strengths */}
            <div className="surface-elevated p-6">
              <h2 className="font-heading text-lg text-foreground mb-4">Key Strengths</h2>
              <ul className="flex flex-col gap-3">
                {expertProfile.keyStrengths.map((strength, i) => (
                  <li key={`strength-${i}`} className="flex items-start gap-3">
                    <span className="text-primary mt-0.5 flex-shrink-0 font-mono-data">◆</span>
                    <span className="text-sm text-foreground leading-relaxed">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sector Exposure */}
            <div className="surface-elevated p-6">
              <h2 className="font-heading text-lg text-foreground mb-4">Sector Exposure</h2>
              <div className="flex flex-wrap gap-2">
                {expertProfile.sectorExposure.map((sector) => (
                  <span key={`sector-${sector}`} className="text-xs font-mono-data px-3 py-1.5 rounded-lg bg-secondary/8 text-secondary border border-secondary/15">
                    {sector}
                  </span>
                ))}
              </div>
            </div>

            {/* Engagement Types */}
            <div className="surface-elevated p-6">
              <h2 className="font-heading text-lg text-foreground mb-4">Engagement Types</h2>
              <div className="flex flex-wrap gap-2">
                {expertProfile.engagementTypes.map((et) => (
                  <span key={`et-${et}`} className="text-xs font-mono-data px-3 py-1.5 rounded-lg bg-muted text-foreground border border-border">
                    {et}
                  </span>
                ))}
              </div>
            </div>

            {/* Rating Breakdown */}
            <div className="surface-elevated p-6">
              <h2 className="font-heading text-lg text-foreground mb-6">Rating Breakdown</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="h-64">
                  <RatingRadarChart dimensions={expertProfile.ratingDimensions} />
                </div>
                <div className="flex flex-col gap-3">
                  {Object.entries(expertProfile.ratingDimensions).map(([dim, score]) => (
                    <div key={`dim-${dim}`} className="flex items-center gap-3">
                      <span className="text-xs font-mono-data text-muted-foreground w-24 capitalize">{dim}</span>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="rating-bar-fill h-full"
                          style={{ width: `${(score / 5) * 100}%` }}
                        />
                      </div>
                      <span className="font-mono-data text-sm font-semibold text-foreground w-8 text-right">{score.toFixed(1)}</span>
                    </div>
                  ))}
                  <div className="pt-3 border-t border-border flex items-center justify-between">
                    <span className="text-xs font-mono-data text-muted-foreground">Overall Average</span>
                    <span className="font-mono-data text-lg font-bold text-primary">{expertProfile.averageRating.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="flex flex-col gap-4">
            {/* Rate Card */}
            <div className="surface-elevated p-5 sticky top-24">
              <h3 className="font-heading text-base text-foreground mb-4">Engagement Rates</h3>

              <div className="flex flex-col gap-3 mb-5">
                <div className="flex items-center justify-between py-2.5 border-b border-border">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock size={14} />
                    Hourly
                  </div>
                  <span className="font-mono-data text-base font-semibold text-foreground">
                    ₹{expertProfile.hourlyRate.toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2.5 border-b border-border">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MessageSquare size={14} />
                    Session (2hr)
                  </div>
                  <span className="font-mono-data text-base font-semibold text-foreground">
                    ₹{expertProfile.sessionRate.toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Zap size={14} />
                    Rush Rate
                  </div>
                  <span className="font-mono-data text-base font-semibold text-foreground">
                    {expertProfile.rushMultiplier}× hourly
                  </span>
                </div>
              </div>

              <button
                onClick={() => setRequestOpen(true)}
                className="w-full py-3 px-4 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 active:scale-95 transition-all duration-150 flex items-center justify-center gap-2"
              >
                <IndianRupee size={14} />
                Request This Expert
              </button>

              <p className="text-[10px] text-muted-foreground text-center mt-3 leading-relaxed">
                Requires buyer account. Agreed rates are set per engagement.
              </p>
            </div>

            {/* Anonymity notice */}
            <div className="surface-elevated p-4">
              <p className="text-xs font-mono-data text-secondary uppercase tracking-wider mb-2">🔒 Identity Protected</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Real name, employer, and institution are permanently masked. This profile was generated by AI from CV content only.
              </p>
            </div>
          </div>
        </div>
      </div>

      <RequestModal
        isOpen={requestOpen}
        onClose={() => setRequestOpen(false)}
        expert={{ pseudonym: expertProfile.pseudonym, hourlyRate: expertProfile.hourlyRate }}
      />
    </>
  );
}